import React from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { LocationContent } from '../../../../types/pageBuilder';
import { DEFAULT_LOCATION_CONTENT } from '../../../../constants/pageBuilderDefaults';
import { useToast } from '../../../ui/use-toast';

interface LocationSectionProps {
  projectId: string;
  selectedLanguage: string;
  locationComponent: any;
  locationContent: LocationContent;
  setLocationContent: React.Dispatch<React.SetStateAction<LocationContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const LocationSection: React.FC<LocationSectionProps> = ({
  projectId,
  selectedLanguage,
  locationComponent,
  locationContent,
  setLocationContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { toast } = useToast();
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');

  const handleSaveLocationComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID and language are required'
      });
      return;
    }

    console.log('Saving location component with data:', locationContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'location',
        order: 600, // Sixth component
        content: locationContent,
        isActive: true,
        componentId: locationComponent?._id
      });
      
      console.log('Location component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Location section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving location component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save location'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a nearby place
  const addNearbyPlace = () => {
    setLocationContent(prev => ({
      ...prev,
      nearbyPlaces: [
        ...prev.nearbyPlaces,
        {
          category: 'lifestyle',
          name: '',
          distance: '',
          time: ''
        }
      ]
    }));
  };

  // Remove a nearby place
  const removeNearbyPlace = (index: number) => {
    setLocationContent(prev => ({
      ...prev,
      nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== index)
    }));
  };

  // Update a nearby place
  const updateNearbyPlace = (index: number, field: keyof LocationContent['nearbyPlaces'][0], value: string) => {
    setLocationContent(prev => ({
      ...prev,
      nearbyPlaces: prev.nearbyPlaces.map((place, i) => {
        if (i === index) {
          return {
            ...place,
            [field]: value
          };
        }
        return place;
      })
    }));
  };

  // Add a key distance
  const addKeyDistance = () => {
    setLocationContent(prev => ({
      ...prev,
      keyDistances: [
        ...prev.keyDistances,
        {
          place: '',
          time: ''
        }
      ]
    }));
  };

  // Remove a key distance
  const removeKeyDistance = (index: number) => {
    setLocationContent(prev => ({
      ...prev,
      keyDistances: prev.keyDistances.filter((_, i) => i !== index)
    }));
  };

  // Update a key distance
  const updateKeyDistance = (index: number, field: keyof LocationContent['keyDistances'][0], value: string) => {
    setLocationContent(prev => ({
      ...prev,
      keyDistances: prev.keyDistances.map((distance, i) => {
        if (i === index) {
          return {
            ...distance,
            [field]: value
          };
        }
        return distance;
      })
    }));
  };

  const resetToDefault = () => {
    setLocationContent(DEFAULT_LOCATION_CONTENT);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Location</CardTitle>
        <CardDescription>
          Define the project location
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="location-project-name">Project Name</Label>
          <Input
            id="location-project-name"
            value={locationContent.projectName}
            onChange={(e) => setLocationContent(prev => ({ ...prev, projectName: e.target.value }))}
            placeholder="Enter project name"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="location-coordinates">Coordinates</Label>
          <Input
            id="location-coordinates"
            value={`${locationContent.coordinates.lat}, ${locationContent.coordinates.lng}`}
            onChange={(e) => setLocationContent(prev => ({
              ...prev,
              coordinates: {
                ...prev.coordinates,
                lat: parseFloat(e.target.value.split(', ')[0]),
                lng: parseFloat(e.target.value.split(', ')[1])
              }
            }))}
            placeholder="Enter latitude, longitude"
          />
        </div>
        
        {/* Nearby Places Section */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Nearby Places</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addNearbyPlace}
              type="button"
            >
              Add Nearby Place
            </Button>
          </div>
          
          {locationContent.nearbyPlaces.length === 0 ? (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-sm text-gray-500">No nearby places added yet. Click the button above to add a place.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {locationContent.nearbyPlaces.map((place, index) => (
                <div key={index} className="border rounded-md p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium">Nearby Place {index + 1}</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeNearbyPlace(index)}
                      className="h-8 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`nearby-place-category-${index}`}>Category</Label>
                      <Select 
                        value={place.category} 
                        onValueChange={(value) => updateNearbyPlace(index, 'category', value)}
                      >
                        <SelectTrigger id={`nearby-place-category-${index}`}>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="lifestyle">Lifestyle</SelectItem>
                          <SelectItem value="transport">Transport</SelectItem>
                          <SelectItem value="worship">Worship</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor={`nearby-place-name-${index}`}>Place Name</Label>
                      <Input
                        id={`nearby-place-name-${index}`}
                        value={place.name}
                        onChange={(e) => updateNearbyPlace(index, 'name', e.target.value)}
                        placeholder="Dubai Mall"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor={`nearby-place-distance-${index}`}>Distance</Label>
                      <Input
                        id={`nearby-place-distance-${index}`}
                        value={place.distance}
                        onChange={(e) => updateNearbyPlace(index, 'distance', e.target.value)}
                        placeholder="2.5 km"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor={`nearby-place-time-${index}`}>Travel Time</Label>
                      <Input
                        id={`nearby-place-time-${index}`}
                        value={place.time}
                        onChange={(e) => updateNearbyPlace(index, 'time', e.target.value)}
                        placeholder="8 mins"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Key Distances Section */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Key Distances</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addKeyDistance}
              type="button"
            >
              Add Key Distance
            </Button>
          </div>
          
          {locationContent.keyDistances.length === 0 ? (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-sm text-gray-500">No key distances added yet. Click the button above to add a distance.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {locationContent.keyDistances.map((distance, index) => (
                <div key={index} className="border rounded-md p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium">Key Distance {index + 1}</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeKeyDistance(index)}
                      className="h-8 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`key-distance-place-${index}`}>Place</Label>
                      <Input
                        id={`key-distance-place-${index}`}
                        value={distance.place}
                        onChange={(e) => updateKeyDistance(index, 'place', e.target.value)}
                        placeholder="Dubai Mall"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor={`key-distance-time-${index}`}>Travel Time</Label>
                      <Input
                        id={`key-distance-time-${index}`}
                        value={distance.time}
                        onChange={(e) => updateKeyDistance(index, 'time', e.target.value)}
                        placeholder="15 mins"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={resetToDefault}
          disabled={isLoading}
        >
          Reset to Default
        </Button>
        <Button 
          onClick={handleSaveLocationComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save Location'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 